import axios from 'axios';
import { buildScannerRouteFromPath } from '@/utils/scan-route';
import { LOOKUP_TIMEOUT_MS, withLookupRetry } from '@/utils/transient-retry';

export const NFCCARD_FALLBACK_PATH = '/home';
export const PENDING_DEEP_LINK_STORAGE_KEY = 'deepLinkPathObj';

const MOBILE_APP_HOSTS = new Set([
  'mobile.heartofgodchurch.org',
  'mobile.dev.heartofgodchurch.org',
  'mobile.stg.heartofgodchurch.org',
]);

// Every host THIS app is actually reopened for. An external deep link must never
// point at one of these: handing it to the system browser would re-trigger our own
// app link and bounce straight back in — an infinite loop.
//
// "Claimed" means the domain's apple-app-site-association names OUR appID
// (4G2HYEZQRL.app.mobile.hogc). The entitlement is only half the contract; iOS
// reopens an app only where the domain agrees. Verified 2026-08-31:
//
//   hogc.one, hogc.us, mobile.*   app.mobile.hogc, paths ['*']     ← ours
//   one.*.heartofgodchurch.org    app.mobile.one.hogc, ['/e-giro/*']
//
// The `one.*` hosts were on this list and are NOT ours — that AASA names a
// different app. This app can never be reopened by them, so blocking them stopped
// nothing and refused every real link on that domain (an external members-only
// resource silently became a trip to /home). AppDebug.entitlements still claims
// them, which is what made it look right; the claim is inert while the domain does
// not name us back.
//
// hogc.link stays: the entitlement claims `hogc.link/nfccard` specifically, and a
// host-level entry is the conservative reading of a path-level claim.
const OWN_APP_HOSTS = new Set([...MOBILE_APP_HOSTS, 'hogc.one', 'hogc.link', 'hogc.us']);

export function isOwnAppHost(urlString) {
  try {
    return OWN_APP_HOSTS.has(new URL(urlString).hostname);
  } catch {
    return false;
  }
}

const INVALID_ROUTE_PATH_NAMES = new Set(['undefined', 'null']);
// The routes that exist only while the app is starting up or authenticating.
// Never a destination in their own right: a member must not be resumed onto one,
// and must not be sent BACK onto one either — a native deep link is pushed on top
// of whichever of these the app happened to be showing, so "something is behind
// us" is true on a cold open without anywhere real being behind us.
export const BOOT_ONLY_PATHS = new Set(['/', '/pre-sign-in', '/login', '/pre-loading']);

export function isMobileAppHost(hostname) {
  return typeof hostname === 'string' && MOBILE_APP_HOSTS.has(hostname);
}

export async function classifyDeepLink(url) {
  let parsedUrl;
  try {
    parsedUrl = new URL(url);
  } catch {
    return { type: 'home', url };
  }

  const slug = parsedUrl.pathname.replace(/^\/+|\/+$/g, '');
  const scannerRoute = buildScannerRouteFromPath(parsedUrl.pathname, url);

  if (isMobileAppHost(parsedUrl.hostname)) {
    if (!slug) {
      return { type: 'home', url };
    }
    const route = scannerRoute || buildDeepLinkRoute(parsedUrl.pathname, url);
    if (!route) {
      return { type: 'home', url };
    }
    return { type: 'direct', route, url };
  }

  if (!slug) {
    return { type: 'home', url };
  }

  if (scannerRoute) {
    return { type: 'direct', route: scannerRoute, url };
  }

  try {
    const data = await fetchDeepLinkPath(slug);
    // External links deliberately leave the app: the app has already opened
    // (whole-domain claim), so we hand off to the system browser, which lets
    // another app's deep link fire (e.g. bible.com -> YouVersion). Skip the
    // hand-off if the destination is one of our own hosts — that would loop.
    if (data?.isExternal && data?.externalUrl && !isOwnAppHost(data.externalUrl)) {
      return { type: 'external', externalUrl: data.externalUrl, url };
    }
    if (data?.path) {
      return {
        type: 'dynamic',
        path: parsedUrl.pathname,
        url,
        _prefetchedDeepLink: data,
      };
    }
    return { type: 'home', url };
  } catch (error) {
    // Every retry is spent by the time we get here, so this is either a slug the
    // server does not know or a network the app never reached. Home either way — but
    // SAY so. The silent version of this line is precisely what made a transient
    // failure indistinguishable from a bad QR.
    console.warn(`[DeepLink] Could not resolve slug ${slug}:`, error?.message);
    return { type: 'home', url };
  }
}

export async function resolveDynamicDeepLink(action) {
  if (!action || action.type !== 'dynamic' || !action.path) {
    return null;
  }

  try {
    let deepLink = action._prefetchedDeepLink;
    if (!deepLink) {
      const lookupKey = action.path.replace(/^\/+/, '');
      deepLink = await fetchDeepLinkPath(lookupKey);
    }
    return buildDeepLinkRoute(deepLink?.path, action.url, { trustScannerPath: true });
  } catch (error) {
    console.warn(`[DeepLink] Failed to resolve dynamic path for ${action.path}:`, error?.message);
    return null;
  }
}

export async function fetchDeepLinkPath(key) {
  return withLookupRetry(
    async () => {
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/deeplink/${key}`, {
        timeout: LOOKUP_TIMEOUT_MS,
      });
      return data;
    },
    {
      onRetry: (error, attempt, delay) =>
        console.warn(
          `[DeepLink] Lookup for ${key} failed (attempt ${attempt + 1}), retrying in ${delay}ms:`,
          error?.message,
        ),
    },
  );
}

export function normalizeDeepLinkPath(path) {
  if (typeof path !== 'string') {
    return '';
  }

  const trimmedPath = path.trim();
  if (!trimmedPath) {
    return '';
  }

  const pathName = trimmedPath.replace(/^\/+|\/+$/g, '').toLowerCase();
  if (INVALID_ROUTE_PATH_NAMES.has(pathName)) {
    return '';
  }

  return trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
}

export function normalizePendingDeepLinkRoute(route) {
  const normalizedPath = normalizeDeepLinkPath(route?.path);
  if (!normalizedPath || BOOT_ONLY_PATHS.has(normalizedPath)) {
    return null;
  }

  const query =
    route?.query && typeof route.query === 'object' && !Array.isArray(route.query)
      ? route.query
      : {};

  return {
    path: normalizedPath,
    query,
    hash: typeof route?.hash === 'string' ? route.hash : '',
  };
}

function getStorage(storage) {
  if (storage) {
    return storage;
  }
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return localStorage;
}

export function clearPendingDeepLinkRoute(storage) {
  const resolvedStorage = getStorage(storage);
  resolvedStorage?.removeItem?.(PENDING_DEEP_LINK_STORAGE_KEY);
}

export function savePendingDeepLinkRoute(route, storage) {
  const resolvedStorage = getStorage(storage);
  const pendingRoute = normalizePendingDeepLinkRoute(route);
  if (!pendingRoute) {
    clearPendingDeepLinkRoute(resolvedStorage);
    return null;
  }

  resolvedStorage?.setItem?.(PENDING_DEEP_LINK_STORAGE_KEY, JSON.stringify(pendingRoute));
  return pendingRoute;
}

export function getPendingDeepLinkRoute(storage, options = {}) {
  const resolvedStorage = getStorage(storage);
  const rawRoute = resolvedStorage?.getItem?.(PENDING_DEEP_LINK_STORAGE_KEY);
  if (!rawRoute) {
    return null;
  }

  let parsedRoute = null;
  try {
    parsedRoute = JSON.parse(rawRoute);
  } catch (_e) {
    parsedRoute = null;
  }

  const route = normalizePendingDeepLinkRoute(parsedRoute);
  if (!route && options.clearInvalid !== false) {
    clearPendingDeepLinkRoute(resolvedStorage);
  }
  return route;
}

export function buildPendingDeepLinkAppState(storage) {
  const route = getPendingDeepLinkRoute(storage);
  return route ? { fullPathObj: route } : null;
}

// Split a stored deep-link path into the parts a route object needs.
//
// `deep_link_config.path` looks like a relative URL and now has to carry two things
// beyond the path itself: `#section`, the anchor a printed booklet QR addresses a
// lesson section by, and `?view=leader`, which picks the Leader's Guide. Both arrive
// inside the one `path` string, and router.push({ path }) does NOT parse them out —
// it navigates to a route whose literal path contains a `?` or `#`, which matches
// nothing and lands the reader on the fallback.
//
// A non-string is left alone so normalizeDeepLinkPath keeps rejecting it as before.
function splitStoredPath(path) {
  if (typeof path !== 'string') {
    return { bare: path, search: '', hash: '' };
  }
  const hashAt = path.indexOf('#');
  const hash = hashAt === -1 ? '' : path.slice(hashAt);
  const withoutHash = hashAt === -1 ? path : path.slice(0, hashAt);
  const queryAt = withoutHash.indexOf('?');
  return {
    bare: queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt),
    search: queryAt === -1 ? '' : withoutHash.slice(queryAt + 1),
    hash,
  };
}

function queryFromSearch(search) {
  const query = {};
  for (const [key, value] of new URLSearchParams(search).entries()) {
    query[key] = value;
  }
  return query;
}

export function buildDeepLinkRoute(path, originalUrl, options = {}) {
  const { bare, search, hash: storedHash } = splitStoredPath(path);
  const normalizedPath = normalizeDeepLinkPath(bare);
  if (!normalizedPath) {
    return null;
  }

  const scannerRoute = buildScannerRouteFromPath(normalizedPath, originalUrl, {
    trustPath: options.trustScannerPath,
  });
  if (scannerRoute) {
    return scannerRoute;
  }

  const storedQuery = queryFromSearch(search);

  if (!originalUrl) {
    return { path: normalizedPath, query: storedQuery, hash: storedHash };
  }

  try {
    const url = new URL(originalUrl);
    return {
      // The scanned URL's own query and hash win where both carry the same key: the
      // stored path is the configured default for that short link, and what the
      // person actually opened is the more specific ask.
      path: normalizedPath,
      query: { ...storedQuery, ...queryFromSearch(url.search) },
      hash: url.hash || storedHash,
    };
  } catch (error) {
    return {
      path: normalizedPath,
      query: storedQuery,
      hash: storedHash,
    };
  }
}
